const _isTicket = (s) =>
  !!(s && s.name && typeof s.name === 'string' && s.name.charAt(0) === '#')
const _isEpic = (s) => !!(s && (s.name || s._counts) && !_isTicket(s))
const _storyHref = (s) =>
  _isEpic(s) ? `/tickets/board?epic=${encodeURIComponent(s.id || '')}` : '/tickets/stories'

let _cssInjected = false
const _injectTicketCSS = () => {
  if (_cssInjected) return
  _cssInjected = true
  const style = document.createElement('style')
  style.textContent =
    '[data-card-type="ticket"] [data-story-only] { display: none !important; }'
  document.head.appendChild(style)
}

export const StoryCard = {
  extends: 'Link',
  href: (el, s) => _storyHref(s),
  attr: {
    'data-card-type': (el, s) =>
      _isTicket(s) ? 'ticket' : _isEpic(s) ? 'epic' : 'story'
  },
  onCreate: () => _injectTicketCSS(),
  flow: 'y',
  round: 'C2',
  background: 'overlay',
  backdropFilter: 'blur(8px)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'line',
  overflow: 'hidden',
  textDecoration: 'none',
  cursor: 'pointer',
  position: 'relative',
  boxShadow:
    'inset 0 1px 0 var(--color-line), 0 8px 32px -16px var(--color-shadow)',
  transition:
    'transform 200ms cubic-bezier(.2,.8,.2,1), box-shadow 200ms ease, border-color 200ms ease, background 200ms ease',
  ':hover': {
    borderColor: 'overlayStrong',
    background: 'overlay',
    transform: 'translateY(-3px)',
    boxShadow:
      'inset 0 1px 0 var(--color-line), 0 16px 48px -8px var(--color-shadow)'
  },
  onClick: (e, el, s) => {
    if (_isTicket(s)) return
    e.preventDefault()
    el.router(_storyHref(s), el.getRoot(), {}, { pushState: true, scrollToTop: true })
  },

  ScHeader: {
    padding: 'B B1 A B1',
    flow: 'y',
    gap: 'Z',

    ScHeaderTitle: {
      tag: 'h3',
      margin: '0',
      fontSize: 'B1',
      fontWeight: '700',
      letterSpacing: '-.01em',
      lineHeight: '1.2',
      color: 'title',
      text: (el, s) => s.title || s.name || ''
    },

    ScHeaderDescription: {
      display: (el, s) => (s.description ? '-webkit-box' : 'none'),
      tag: 'p',
      margin: '0',
      fontSize: 'Z2',
      lineHeight: '1.45',
      color: 'caption',
      overflow: 'hidden',
      webkitLineClamp: '2',
      webkitBoxOrient: 'vertical',
      text: (el, s) => s.description || ''
    }
  },

  ScBody: {
    flow: 'y',
    gap: 'A',
    padding: '0 B1 B1 B1',

    EpicStats: {
      hide: (el, s) => !_isEpic(s) || _isTicket(s),
      flow: 'x',
      align: 'center space-between',
      gap: 'Z',

      Total: {
        flow: 'y',
        gap: 'V',
        Number: {
          tag: 'span',
          fontSize: 'D',
          fontWeight: '700',
          letterSpacing: '-.02em',
          lineHeight: '1',
          color: 'title',
          text: (el, s) => String(s._counts?.total || 0)
        },
        Label: {
          tag: 'span',
          fontSize: 'V',
          fontWeight: '700',
          letterSpacing: '.1em',
          color: 'caption',
          text: 'TICKETS'
        }
      },

      DoneBadge: {
        display: (el, s) => {
          const tot = s._counts?.total || 0
          return tot > 0 ? 'inline-flex' : 'none'
        },
        tag: 'span',
        padding: 'V Z1',
        round: 'C',
        fontSize: 'Y2',
        fontWeight: '700',
        letterSpacing: '.04em',
        color: 'green',
        background: 'color-mix(in srgb, var(--color-green) 16%, transparent)',
        text: (el, s) => {
          const c = s._counts?.byColumn || {}
          const tot = s._counts?.total || 0
          const done = c.eede5560 || 0
          return tot ? `${Math.round((done / tot) * 100)}% DONE` : ''
        }
      }
    },

    EpicBreakdown: {
      display: (el, s) =>
        _isEpic(s) && (s._counts?.total || 0) > 0 ? 'flex' : 'none',
      flow: 'x',
      width: '100%',
      height: 'Z',
      round: 'C',
      overflow: 'hidden',
      background: 'overlay',

      childExtends: 'StoryCardSegment',
      childrenAs: 'state',
      children: (el, s) => {
        // `el.parent.parent` is the card element whose state is the epic row.
        // During the first reactive pass the parent chain may not be fully
        // attached yet — DOMQL runs `children:` factories eagerly so it can
        // diff on the next pass, and at that moment `el.parent` can be null.
        // Guard with optional chaining instead of assuming a two-hop walk.
        const epic = el?.parent?.parent?.state
        if (!epic) return []
        const counts = epic._counts?.byColumn || {}
        const total = epic._counts?.total || 0
        if (!total) return []
        const cols = el?.state?.root?.columns || []
        return cols
          .filter((c) => counts[c.key] > 0)
          .map((c) => ({
            key: c.key,
            label: c.label,
            color: c.color,
            count: counts[c.key],
            pct: (counts[c.key] / total) * 100
          }))
      }
    },

    TicketInfo: {
      display: 'none',
      onRender: (el, s) => {
        if (el.node && _isTicket(s)) el.node.style.display = 'flex'
      },
      flow: 'y',
      gap: 'Z',
      TicketDescription: {
        tag: 'p',
        margin: '0',
        fontSize: 'Z2',
        lineHeight: '1.45',
        color: 'caption',
        text: (el, s) => s.description || ''
      }
    },

    ScMembers: {
      attr: { 'data-story-only': 'true' },
      flow: 'y',
      gap: 'Z',

      ScStack: {
        extends: 'AvatarBundle',
        emails: (el, s) => s.members || []
      },
      ScCaption: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'caption',
        text: (el, s) => `${(s.members || []).length} people joined`
      }
    },

    ScMeeting: {
      attr: { 'data-story-only': 'true' },
      flow: 'y',
      gap: 'Y',

      ScMeetingTitle: {
        flow: 'x',
        align: 'center',
        gap: 'Z',
        ScMeetingIcon: {
          extends: 'Icon',
          icon: 'date',
          boxSize: 'A',
          color: 'green'
        },
        ScMeetingText: {
          tag: 'span',
          fontSize: 'A',
          fontWeight: '700',
          color: 'green',
          text: (el, s) => s.meeting_title || s.meetingTitle
        }
      },
      ScWhen: {
        flow: 'x',
        gap: 'Z',
        align: 'baseline',
        ScWhenSub: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: 'Planned for'
        },
        ScWhenDay: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'title',
          fontWeight: '700',
          text: (el, s) => s.meeting_date || s.meetingDate || ''
        },
        ScWhenAt: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          text: (el, s) => `· at ${s.meeting_time || s.meetingTime || ''}`
        }
      }
    }
  }
}

export const StoryCardSegment = {
  tag: 'span',
  height: '100%',
  background: (el, s) =>
    `var(--color-${el.call('githubColorTokenFromName', s.color)})`,
  opacity: '.85',
  width: (el, s) => `${s.pct || 0}%`,
  title: (el, s) => `${s.label || ''} · ${s.count || 0}`,
  ':hover': { opacity: '1' }
}
